import React, { useMemo } from 'react';
import {
  createSearchParams, Navigate, NavigateFunction, NavLink, useLocation, useNavigate,
} from 'react-router-dom';
import { AnyObject } from '@triare/auth-redux';
import {
  FileTextOutlined,
  LogoutOutlined, SendOutlined, SettingFilled, SolutionOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Menu as AntdMenu } from 'antd';
import { connect } from 'react-redux';
import { signOut } from '@triare/auth-redux/dist/saga/auth/signOut';
import { Route } from '../../../routes';
import store, { RootState } from '../../../store';
import { moduleName, useAuth, User } from '../../../store/auth';
import { doesUserHaveAccess } from '../../../routes/PrivateRoute';
import SignIn from '../../../pages/Auth/SignIn';
import ForgotPassword from '../../../pages/Auth/ForgotPassword';
import ResetPassword from '../../../pages/Auth/ResetPassword';
import ConfirmEmail from '../../../pages/Auth/ConfirmEmail';
import GeneralSettings from '../../../pages/GeneralSettings';
import ReferralTemplates from '../../../pages/ReferralTemplates';
import Users from '../../../pages/Users';
import Admins from '../../../pages/Admins';
import UserEdit from '../../../pages/Users/view';
import AdminCreate from '../../../pages/Admins/create';
import AdminEdit from '../../../pages/Admins/view';
import BusinessPartners from '../../../pages/BusinessPartners';
import BusinessPartnerView from '../../../pages/BusinessPartners/view';
import InviteUsers from '../../../pages/InviteUsers';
import ConfirmOTP from '../../../pages/Auth/ConfirmOTP';
import Tariffs from '../../../pages/Tariffs';
import TariffsCreate from '../../../pages/Tariffs/create';
import TariffsEdit from '../../../pages/Tariffs/edit';
import Logs from '../../../pages/Logs';
import { UserRole } from '../../../enums/user';

import styles from './index.module.scss';

export interface MenuRoute extends Route {
  ignoreMenu?: boolean;

  children?: MenuRoute[];
}

export const authRoutes: MenuRoute[] = [
  {
    name: 'Sign In',
    route: {
      path: 'sign-in',
      element: <SignIn />,
    },
  },
  /* {
    name: 'Sign Up',
    // align: 'left',
    route: {
      path: 'sign-up',
      element: <SignUp />,
    },
    children: [
      {
        name: 'Information',
        route: {
          path: 'information',
          element: <Information />,
        },
      },
    ],
  }, */
  {
    name: 'Forgot password',
    route: {
      path: 'forgot-password',
      element: <ForgotPassword />,
    },
  },
  {
    name: 'Reset password',
    route: {
      path: 'reset-password/:secretKey',
      element: <ResetPassword />,
    },
  },
  {
    name: 'Confirm email',
    route: {
      path: 'complete-registration/:mode/:secretKey',
      element: <ConfirmEmail />,
    },
  },
  // TODO: remove after
  /* for test
  {
    name: 'Confirm email (set a password)',
    // align: 'left',
    route: {
      path: 'complete-registration-test/password/:secretKey', // TODO change to complete-registration/ ?
      element: <ConfirmEmail mode="password" />,
    },
  },
   */
  {
    name: 'OTP Confirm',
    route: {
      path: 'auto-sign-in-otp/:secretKey',
      element: <ConfirmOTP />,
    },
  },
];

export const routes: MenuRoute[] = [
  ...authRoutes,
  {
    // ignoreMenu: true,
    privateRoute: true,
    name: 'General Settings',
    align: 'left',
    icon: <SettingFilled />,
    route: {
      path: 'general-settings',
      element: <GeneralSettings />,
    },
    // children: [
    //   {
    //     // ignoreMenu: true,
    //     privateRoute: true,
    //     // ignoreMenu: false,
    //     name: 'Example w inner',
    //     // align: 'left',
    //     icon: <MailFilled />,
    //     route: {
    //       path: 'inner',
    //       element: <div>Example with inner menu item</div>,
    //     },
    //   },
    // ],
  },
  {
    privateRoute: true,
    name: 'Referral Templates',
    align: 'left',
    icon: <SendOutlined />,
    route: {
      path: 'referral-templates',
      element: <ReferralTemplates />,
    },
  },
  {
    privateRoute: true,
    roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
    name: 'Users',
    align: 'left',
    icon: <UserOutlined />,
    route: {
      path: 'users',
      element: <Users />,
    },
    children: [
      {
        // hidden: true,
        ignoreMenu: true,
        roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
        privateRoute: true,
        name: 'View user',
        route: {
          path: ':id',
          element: <Navigate to="edit" replace />, // <NotFound />,
        },
        children: [
          {
            ignoreMenu: true,
            roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
            privateRoute: true,
            name: 'Edit user',
            route: {
              path: 'edit',
              element: <UserEdit />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    roles: [UserRole.ROOT],
    name: 'Admins',
    align: 'left',
    icon: <UserOutlined />,
    route: {
      path: 'admins',
      element: <Admins />,
    },
    children: [
      {
        ignoreMenu: true,
        roles: [UserRole.ROOT],
        privateRoute: true,
        name: 'Create admin',
        route: {
          path: 'create',
          element: <AdminCreate />,
        },
      },
      {
        ignoreMenu: true,
        roles: [UserRole.ROOT],
        privateRoute: true,
        name: 'View admin',
        route: {
          path: ':id',
          element: <Navigate to="edit" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            roles: [UserRole.ROOT],
            privateRoute: true,
            name: 'Edit admin',
            route: {
              path: 'edit',
              element: <AdminEdit />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
    name: 'Business Partners',
    align: 'left',
    icon: <SolutionOutlined />,
    route: {
      path: 'business-partners',
      element: <BusinessPartners />,
    },
    children: [
      {
        ignoreMenu: true,
        roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
        privateRoute: true,
        name: 'View Business partner',
        route: {
          path: ':id',
          element: <Navigate to="edit" replace />, // <NotFound />,
        },
        children: [
          {
            ignoreMenu: true,
            roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
            privateRoute: true,
            name: 'Edit Business partner',
            route: {
              path: 'edit',
              element: <BusinessPartnerView />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    roles: [UserRole.ADMIN, UserRole.ROOT],
    name: 'Invite users',
    align: 'left',
    icon: <SendOutlined />,
    route: {
      path: 'invite-users',
      element: <InviteUsers />,
    },
  },
  {
    privateRoute: true,
    roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
    name: 'Tariffs',
    align: 'left',
    icon: <TableOutlined />,
    route: {
      path: 'tariffs',
      element: <Tariffs />,
    },
    children: [
      {
        roles: [UserRole.ADMIN, UserRole.ROOT],
        ignoreMenu: true,
        privateRoute: true,
        name: 'Create Tariff',
        route: {
          path: 'create',
          element: <TariffsCreate />,
        },
      },
      {
        ignoreMenu: true,
        roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
        privateRoute: true,
        name: 'Tariff redirect to edit',
        route: {
          path: ':id',
          element: <Navigate to="edit" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            roles: [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
            privateRoute: true,
            name: 'Edit Tariff',
            route: {
              path: 'edit',
              element: <TariffsEdit />,
            },
          },
        ],
      },

    ],
  },
  {
    privateRoute: true,
    roles: ['admin', 'root'],
    name: 'Logs',
    align: 'left',
    icon: <FileTextOutlined />,
    route: {
      path: 'logs',
      element: <Logs />,
    },
    children: [],
  },
  {
    privateRoute: true,
    align: 'right',
    icon: <UserOutlined style={{ fontSize: 18 }} />,
    name: 'Account',
    disabled: () => true,
    route: {
      path: 'account',
      element: <div>Account page</div>,
    },
  },
  {
    align: 'right',
    icon: <LogoutOutlined />,
    name: 'Log out',
    onClick: () => {
      store.dispatch(signOut());
    },
  },
];

export const paramsToString = (user?: User | null, params?: ((user: User) => string) | AnyObject | string) => {
  if (!user) {
    return '';
  }

  if (typeof params === 'string') {
    return '?params';
  }

  if (typeof params === 'function') {
    let result = params(user);

    if (result) {
      if (typeof result === 'object') {
        result = createSearchParams(result).toString();
      }

      return `?${result}`;
    }

    return '';
  }

  if (params && typeof params === 'object') {
    return `?${createSearchParams(params).toString()}`;
  }

  return '';
};

const createItem = (navigate: NavigateFunction, {
  route, children, ignoreMenu, privateRoute, ...data
}: Route, user: User) => {
  let childrenList;

  // && children.every((item) => !item.hidden)
  if (children && children.length > 0) {
    childrenList = children.filter(({ ignoreMenu: childrenIgnoreMenu }) => childrenIgnoreMenu !== true);

    if (childrenList.length > 0) {
      childrenList = childrenList.map(
        ({ ignoreMenu: childrenIgnoreMenu, privateRoute: childrenPrivateRoute, ...child }) => ({
          ...child,
          children: undefined,
          label: child.route ? (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <NavLink to={`/${child.route.path}${paramsToString(user, child.route.path) || ''}`}>
              {child.name || child.key}
            </NavLink>
          ) : (
            child.name
          ),
          key: child.route ? `/${child.route.path}` : `/${child.key || child.name}`,
        }),
      );
    }
  }

  const name = typeof data.name === 'function' ? data.name(user) : data.name;

  return {
    ...data,
    children: childrenList && childrenList.length > 0 ? childrenList : undefined,
    label: route && (childrenList || []).length === 0 ? (
      <NavLink to={`/${route.path}${paramsToString(user, route.params) || ''}`}>{name}</NavLink>
    ) : (
      name
    ),
    key: route ? `/${route.path}` : `/${data.key || name}`,
  };

  // TODO fix when menu collapsed - it dont work onClick because NavLink hides and only icon shown.
  // When menu collapsed add onClick on elements
  // if (route) {
  //   item.onClick = () => navigate(`/${route.path}${paramsToString(user, route.params)}`);
  // }
  //
  // return item;
};

export const menuLeft = routes.filter(({ ignoreMenu }) => ignoreMenu !== true).filter(({ align }) => align === 'left');

export const menuRight = routes
  .filter(({ ignoreMenu }) => ignoreMenu !== true)
  .filter(({ align }) => align === 'right');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filter(list: any[], user: User) {
  const newList = list.map((item) => ({ ...item, children: item.children ? [...item.children] : undefined }));

  return newList.filter((item) => {
    if (item && item.children && item.children.length > 0) {
      // eslint-disable-next-line no-param-reassign
      item.children = filter(item.children, user);
    }

    return item?.roles ? doesUserHaveAccess(item?.roles, user) : true;
  });
}

export interface MenuProps {
  user: User;
}

function Menu({ user }: MenuProps): React.ReactNode {
  const navigate = useNavigate();
  const { authorized } = useAuth();
  const { pathname } = useLocation();
  const menuLeftFiltered = useMemo(
    () => filter(
      menuLeft
        .filter(({ roles }) => !roles || roles.includes(user?.role))
        .map((data) => createItem(navigate, data, user)),
      user,
    ).map((item) => ({
      ...item,
      name: typeof item.name === 'function' ? item.name(user) : item.name,
      icon: typeof item.icon === 'function' ? item.icon(user) : item.icon,
    })), // .map(({ children, ...item }) => item)
    [user],
  );
  const menuRightFiltered = useMemo(() => {
    const list = filter(
      menuRight
        .filter(({ roles }) => !roles || roles.includes(user?.role))
        .map((data) => createItem(navigate, data, user)),
      user,
    ).map((item) => ({
      ...item,
      name: typeof item.name === 'function' ? item.name(user) : item.name,
      icon: typeof item.icon === 'function' ? item.icon(user) : item.icon,
    }));

    // if (list[2] && list[2].children && list[2].children[3]) {
    //   list[2].children[3].onClick = () => {
    //     open({
    //       icon: <LogoutOutlined style={{ color: '#FF4D4F' }} />,
    //       title: 'Log out',
    //       content: 'Are you sure you want to Log out?',
    //       cancelText: 'Cancel',
    //       centered: true,
    //       okText: 'Yes',
    //       onOk: () => {
    //         store.dispatch(signOut());
    //         navigate('/');
    //       },
    //     });
    //   };
    // }

    return list;
  }, [user]);

  const activeMenuItem = `/${pathname.split('/')[1]}`;

  return (
    <div className={styles.menu}>
      <AntdMenu
        key={`top-${activeMenuItem}`}
        theme="light"
        mode="inline" // "vertical"
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        defaultSelectedKeys={[activeMenuItem]}
        selectedKeys={[activeMenuItem]}
        items={menuLeftFiltered}
      />
      {authorized ? (
        <AntdMenu
          key={`bottom-${activeMenuItem}`}
          className={styles.rightMenu}
          theme="light"
          mode="vertical"
          defaultSelectedKeys={[activeMenuItem]}
          selectedKeys={[activeMenuItem]}
          items={menuRightFiltered}
        />
      ) : 'not authorized'}
    </div>
  );
}

export default connect((state: RootState) => ({
  user: state[moduleName].user,
}))(Menu);
